import { apiClient } from './api-client';

export type OwnershipType = 'COMPANY' | 'CUSTOMER';
export type EquipmentStatus = 'ACTIVE' | 'INACTIVE' | 'RETIRED';

export interface Equipment {
  id: string;
  tenantId: string;
  ownershipType: OwnershipType;
  customerId: string | null;
  /**
   * D-101 — customer identity resolved by the Equipment API itself.
   *
   * Both are returned so the caller can apply `organizationName ?? name`
   * without the API pretending a legacy short name is a registered
   * organisation name. Both are null for company-owned equipment.
   */
  customerOrganizationName: string | null;
  customerName: string | null;
  /**
   * D-102 (Stage B) - where this machine is right now, DERIVED server-side
   * at read time from existing inspection and inward records. Never stored,
   * never sent by the client, and never a second status column in the
   * database. `workflowStage` is null when the equipment has never entered
   * the service process.
   */
  workflowStage: string | null;
  workflowGroup: EquipmentWorkflowGroup;
  /**
   * D-104 (Stage D) - the inspection the D-102 rule already selected as
   * authoritative. Present so the stage can link to the record it describes;
   * the browser never works out which record that is.
   */
  workflowInspectionId: string | null;
  /**
   * D-104 - the current inward. Display-only: this application has no inward
   * DETAIL route, only a list page, so there is nothing safe to link to.
   */
  inwardId: string | null;
  inwardNumber: string | null;
  inwardDate: string | null;
  manufacturerId: string;
  equipmentTypeId: string;
  equipmentModelId: string;
  modelNumber: string | null;
  equipmentName: string;
  assetNumber: string;
  serialNumber: string;
  purchaseDate: string | null;
  installationDate: string | null;
  status: EquipmentStatus;
  description: string | null;
  notes: string | null;
  qrCodeToken: string;
  qrCodeImageUrl: string | null;
  qrCodeGeneratedAt: string | null;
  labelPrintedCount: number;
  lastLabelPrintedAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EquipmentQrInfo {
  equipmentId: string;
  qrCodeToken: string;
  qrCodeImageUrl: string | null;
  qrCodeGeneratedAt: string | null;
  labelPrintedCount: number;
  lastLabelPrintedAt: string | null;
}

export type EquipmentWorkflowGroup = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED';

/**
 * D-103 (Stage C) — counts over the COMPLETE filtered population, never the
 * current page. Derived server-side from Stage B's workflow classification; the
 * browser never recomputes a workflow group.
 *
 * `workflowCountsAvailable` is false when the matching population exceeded the
 * server's aggregation cap; the counts are then null rather than misleading.
 */
export interface EquipmentWorkflowSummary {
  total: number;
  pending: number | null;
  inProgress: number | null;
  completed: number | null;
  workflowCountsAvailable: boolean;
}

/** One organisation's split. `customerId` is null for the Company Inventory group. */
export interface EquipmentCustomerSummaryRow {
  customerId: string | null;
  customerOrganizationName: string | null;
  customerName: string | null;
  total: number;
  pending: number;
  inProgress: number;
  completed: number;
}

export interface EquipmentListResponse {
  items: Equipment[];
  page: number;
  pageSize: number;
  total: number;
  summary: EquipmentWorkflowSummary;
  /**
   * Scoped by every active filter EXCEPT customerId and workflowGroup, so it
   * doubles as the tenant-scoped option list for the customer filter. Null when
   * the server could not aggregate.
   */
  customerSummary: EquipmentCustomerSummaryRow[] | null;
}

export interface EquipmentSearchResult {
  id: string;
  equipmentName: string;
  assetNumber: string;
  serialNumber: string;
  equipmentModelId: string;
  modelNumber: string | null;
  customerId: string | null;
  customerName: string | null;
}

export interface EquipmentSearchResponse {
  items: EquipmentSearchResult[];
}

export interface CreateEquipmentPayload {
  ownershipType: OwnershipType;
  customerId?: string;
  manufacturerId: string;
  equipmentTypeId: string;
  equipmentModelId: string;
  equipmentName?: string;
  assetNumber: string;
  serialNumber: string;
  purchaseDate?: string;
  installationDate?: string;
  status?: EquipmentStatus;
  description?: string;
  notes?: string;
}

export type UpdateEquipmentPayload = Partial<CreateEquipmentPayload>;

export const equipmentService = {
  list: (params: {
    page?: number;
    pageSize?: number;
    search?: string;
    ownershipType?: OwnershipType;
    customerId?: string;
    manufacturerId?: string;
    equipmentTypeId?: string;
    equipmentModelId?: string;
    status?: EquipmentStatus;
    // D-103 — both are applied server-side; the browser never filters locally.
    workflowGroup?: EquipmentWorkflowGroup;
    receivingLotId?: string;
    // D-105 — tenant-local calendar dates; the server converts them to instants
    // using the tenant timezone. Half-open [from, to) with `to` fully included.
    receivedFrom?: string;
    receivedTo?: string;
    workflowStage?: string;
  }): Promise<EquipmentListResponse> =>
    apiClient.get<EquipmentListResponse>('/equipment', { params }).then((r) => r.data),

  /**
   * D-105 (Stage E) — download the filtered dataset.
   *
   * The SAME filter object the list uses is sent, so the file cannot contain a
   * different population from the screen. Follows the existing downloadLabel
   * pattern: blob in, object URL, click, revoke. Nothing is stored anywhere.
   */
  exportReport: async (
    format: 'pdf' | 'excel',
    params: Record<string, string | number | undefined>,
  ): Promise<void> => {
    const response = await apiClient.get(`/equipment/export/${format}`, {
      params,
      responseType: 'blob',
    });
    const disposition = String(response.headers?.['content-disposition'] ?? '');
    const filename =
      /filename="?([^";]+)"?/.exec(disposition)?.[1] ??
      `equipment-report.${format === 'pdf' ? 'pdf' : 'xlsx'}`;

    const url = URL.createObjectURL(response.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  search: (q: string, limit = 20): Promise<EquipmentSearchResponse> =>
    apiClient
      .get<EquipmentSearchResponse>('/equipment/search', { params: { q, limit } })
      .then((r) => r.data),

  get: (id: string): Promise<Equipment> =>
    apiClient.get<Equipment>(`/equipment/${id}`).then((r) => r.data),

  scanByToken: (token: string): Promise<Equipment> =>
    apiClient.get<Equipment>(`/equipment/scan/${token}`).then((r) => r.data),

  getQr: (id: string): Promise<EquipmentQrInfo> =>
    apiClient.get<EquipmentQrInfo>(`/equipment/${id}/qr`).then((r) => r.data),

  regenerateQr: (id: string): Promise<EquipmentQrInfo> =>
    apiClient.post<EquipmentQrInfo>(`/equipment/${id}/qr/regenerate`).then((r) => r.data),

  downloadLabel: async (id: string, assetNumber: string): Promise<void> => {
    const response = await apiClient.get(`/equipment/${id}/label`, { responseType: 'blob' });
    const url = URL.createObjectURL(response.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `label-${assetNumber}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  },

  create: (payload: CreateEquipmentPayload): Promise<Equipment> =>
    apiClient.post<Equipment>('/equipment', payload).then((r) => r.data),

  update: (id: string, payload: UpdateEquipmentPayload): Promise<Equipment> =>
    apiClient.patch<Equipment>(`/equipment/${id}`, payload).then((r) => r.data),

  remove: (id: string): Promise<void> => apiClient.delete(`/equipment/${id}`).then(() => undefined),
};
